import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Route, Plus, Trash2, Edit, MapPin, Truck, Users, X, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const PURPOSE_STYLES = {
  business:    { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Business' },
  delivery:    { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Delivery' },
  collection:  { bg: 'bg-teal-100',   text: 'text-teal-700',   label: 'Collection' },
  site_visit:  { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Site Visit' },
  personal:    { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Personal' },
  maintenance: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Maintenance' },
  other:       { bg: 'bg-slate-100',  text: 'text-slate-600',  label: 'Other' },
};

const emptyForm = () => ({
  vehicle_id: '',
  driver_id: '',
  date: new Date().toISOString().split('T')[0],
  start_odometer: '',
  end_odometer: '',
  purpose: 'business',
  route_from: '',
  route_to: '',
  notes: '',
});

export default function TripsPage() {
  const [trips, setTrips]                 = useState([]);
  const [vehicles, setVehicles]           = useState([]);
  const [drivers, setDrivers]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [filterDriver, setFilterDriver]   = useState('all');
  const [addOpen, setAddOpen]             = useState(false);
  const [editOpen, setEditOpen]           = useState(false);
  const [deleteOpen, setDeleteOpen]       = useState(false);
  const [selected, setSelected]           = useState(null);
  const [form, setForm]                   = useState(emptyForm());
  const [saving, setSaving]               = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tripsData, vehData, drvData] = await Promise.all([
        api.getTrips(), api.getVehicles(), api.getDrivers()
      ]);
      setTrips(tripsData.map(t => ({ ...t, date: t.start_date, route_from: t.start_location, route_to: t.end_location })));
      setVehicles(vehData);
      setDrivers(drvData);
    } catch { toast.error('Failed to load trips'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateForm = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // When vehicle is selected, try to populate start odometer from vehicle's current reading
  const handleVehicleSelect = (vehicleId) => {
    updateForm('vehicle_id', vehicleId);
    const v = vehicles.find(v => v.id === vehicleId);
    if (v && v.current_odometer && !form.start_odometer) {
      updateForm('start_odometer', v.current_odometer.toString());
    }
  };

  const calcDistance = () => {
    const s = parseFloat(form.start_odometer);
    const e = parseFloat(form.end_odometer);
    if (!isNaN(s) && !isNaN(e) && e >= s) return (e - s).toFixed(1);
    return null;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error('Please select a vehicle'); return; }
    if (!form.start_odometer || !form.end_odometer) { toast.error('Start and end odometer are required'); return; }
    const start = parseFloat(form.start_odometer);
    const end = parseFloat(form.end_odometer);
    if (end < start) { toast.error('End odometer must be >= start odometer'); return; }
    setSaving(true);
    try {
      await api.createTrip({
        vehicle_id: form.vehicle_id,
        driver_id: form.driver_id || null,
        start_date: form.date,
        start_odometer: start,
        end_odometer: end,
        purpose: form.purpose,
        start_location: form.route_from || null,
        end_location: form.route_to || null,
        notes: form.notes || null,
      });
      toast.success('Trip logged');
      setAddOpen(false);
      setForm(emptyForm());
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save trip'); }
    finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const start = parseFloat(form.start_odometer);
    const end = parseFloat(form.end_odometer);
    if (end < start) { toast.error('End odometer must be >= start odometer'); return; }
    setSaving(true);
    try {
      await api.updateTrip(selected.id, {
        driver_id: form.driver_id || null,
        start_date: form.date,
        start_odometer: start,
        end_odometer: end,
        purpose: form.purpose,
        start_location: form.route_from || null,
        end_location: form.route_to || null,
        notes: form.notes || null,
      });
      toast.success('Trip updated');
      setEditOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.deleteTrip(selected.id);
      toast.success('Trip deleted');
      setDeleteOpen(false);
      setSelected(null);
      setTrips(prev => prev.filter(t => t.id !== selected.id));
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (trip) => {
    setSelected(trip);
    setForm({
      vehicle_id: trip.vehicle_id,
      driver_id: trip.driver_id || '',
      date: trip.date,
      start_odometer: trip.start_odometer?.toString() || '',
      end_odometer: trip.end_odometer?.toString() || '',
      purpose: trip.purpose || 'business',
      route_from: trip.route_from || '',
      route_to: trip.route_to || '',
      notes: trip.notes || '',
    });
    setEditOpen(true);
  };

  const filtered = useMemo(() => trips.filter(t => {
    if (filterVehicle !== 'all' && t.vehicle_id !== filterVehicle) return false;
    if (filterDriver !== 'all' && t.driver_id !== filterDriver) return false;
    return true;
  }), [trips, filterVehicle, filterDriver]);

  const totalDistance = useMemo(() => filtered.reduce((sum, t) => sum + (t.distance || 0), 0), [filtered]);

  const activeDrivers = useMemo(() => drivers.filter(d => d.status === 'active'), [drivers]);

  // Form captures closure state - extracting would require prop-drilling many handlers
  // eslint-disable-next-line react/no-unstable-nested-components
  const TripForm = ({ onSubmit, isEdit = false }) => {
    const dist = calcDistance();
    return (
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Vehicle *</Label>
            <Select value={form.vehicle_id} onValueChange={isEdit ? (v) => updateForm('vehicle_id', v) : handleVehicleSelect} disabled={isEdit}>
              <SelectTrigger data-testid="trip-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
              <SelectContent>
                {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.name} ({v.registration})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Driver</Label>
            <Select value={form.driver_id || 'none'} onValueChange={v => updateForm('driver_id', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="trip-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No driver</SelectItem>
                {activeDrivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date *</Label>
            <Input type="date" value={form.date} onChange={e => updateForm('date', e.target.value)} data-testid="trip-date-input" />
          </div>
          <div className="space-y-2">
            <Label>Purpose</Label>
            <Select value={form.purpose} onValueChange={v => updateForm('purpose', v)}>
              <SelectTrigger data-testid="trip-purpose-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PURPOSE_STYLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Start Odometer (km) *</Label>
            <Input type="number" step="0.1" value={form.start_odometer} onChange={e => updateForm('start_odometer', e.target.value)} placeholder="0" className="font-data" data-testid="trip-start-odo" />
          </div>
          <div className="space-y-2">
            <Label>End Odometer (km) *</Label>
            <Input type="number" step="0.1" value={form.end_odometer} onChange={e => updateForm('end_odometer', e.target.value)} placeholder="0" className="font-data" data-testid="trip-end-odo" />
          </div>
          <div className="space-y-2">
            <Label>Distance</Label>
            <div className={`h-9 flex items-center px-3 rounded-md border text-sm font-data ${dist !== null ? 'bg-green-50 border-green-200 text-green-700 font-bold' : 'bg-gray-50 border-gray-200 text-gray-400'}`} data-testid="trip-distance-display">
              {dist !== null ? `${dist} km` : '--'}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>From</Label>
            <Input value={form.route_from} onChange={e => updateForm('route_from', e.target.value)} placeholder="e.g., Warehouse, Cape Town" data-testid="trip-from-input" />
          </div>
          <div className="space-y-2">
            <Label>To</Label>
            <Input value={form.route_to} onChange={e => updateForm('route_to', e.target.value)} placeholder="e.g., Client Site, Durban" data-testid="trip-to-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={e => updateForm('notes', e.target.value)} placeholder="Additional trip notes..." rows={2} data-testid="trip-notes-input" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => isEdit ? setEditOpen(false) : setAddOpen(false)}>Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-gray-800 hover:bg-gray-700" data-testid="save-trip-btn">
            {saving ? 'Saving...' : isEdit ? 'Update Trip' : 'Log Trip'}
          </Button>
        </div>
      </form>
    );
  };

  if (loading) return <div className="p-6 text-center text-gray-400">Loading trips...</div>;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="trips-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Trip Log</h1>
          <p className="text-sm text-gray-500 mt-1">Track vehicle trips with odometer readings and distance</p>
        </div>
        <Button onClick={() => { setForm(emptyForm()); setAddOpen(true); }} className="bg-gray-800 hover:bg-gray-700" data-testid="add-trip-btn">
          <Plus className="w-4 h-4 mr-2" />Log Trip
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Trips', value: trips.length, cls: 'text-gray-900', bg: 'bg-gray-50' },
          { label: 'This Month', value: trips.filter(t => t.date >= new Date().toISOString().slice(0, 7)).length, cls: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Total Distance', value: `${totalDistance.toLocaleString()} km`, cls: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Vehicles Used', value: new Set(trips.map(t => t.vehicle_id)).size, cls: 'text-purple-600', bg: 'bg-purple-50' },
        ].map(c => (
          <Card key={c.label} className={`border-gray-200 ${c.bg}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">{c.label}</p>
              <p className={`text-2xl font-heading font-bold ${c.cls}`} data-testid={`trip-stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select value={filterVehicle} onValueChange={setFilterVehicle}>
          <SelectTrigger className="w-[200px]" data-testid="filter-trip-vehicle"><SelectValue placeholder="All vehicles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vehicles</SelectItem>
            {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterDriver} onValueChange={setFilterDriver}>
          <SelectTrigger className="w-[180px]" data-testid="filter-trip-driver"><SelectValue placeholder="All drivers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Drivers</SelectItem>
            {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filterVehicle !== 'all' || filterDriver !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterVehicle('all'); setFilterDriver('all'); }}>
            <X className="w-4 h-4 mr-1" />Clear filters
          </Button>
        )}
      </div>

      {/* Trip List */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Route className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">{trips.length === 0 ? 'No trips logged yet' : 'No trips match filters'}</p>
              {trips.length === 0 && <p className="text-sm mt-1">Click &quot;Log Trip&quot; to record your first trip</p>}
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {filtered.map(trip => {
                const pCfg = PURPOSE_STYLES[trip.purpose] || PURPOSE_STYLES.other;
                return (
                  <div key={trip.id} className="flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors" data-testid={`trip-${trip.id}`}>
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${pCfg.bg}`}>
                      <Route className={`w-5 h-5 ${pCfg.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{trip.vehicle_name}</span>
                        <Badge className={`text-xs ${pCfg.bg} ${pCfg.text}`}>{pCfg.label}</Badge>
                        <span className="text-sm font-data font-bold text-green-600">{trip.distance?.toFixed(1)} km</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-sm text-gray-600">
                        {trip.route_from && <span>{trip.route_from}</span>}
                        {trip.route_from && trip.route_to && <ArrowRight className="w-3 h-3 text-gray-400" />}
                        {trip.route_to && <span>{trip.route_to}</span>}
                        {!trip.route_from && !trip.route_to && (
                          <span className="font-data text-gray-400">{trip.start_odometer?.toLocaleString()} — {trip.end_odometer?.toLocaleString()} km</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                        <span>{trip.date}</span>
                        {trip.driver_name && <span>• {trip.driver_name}</span>}
                        {trip.notes && <span>• {trip.notes}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(trip)} data-testid={`edit-trip-${trip.id}`} title="Edit"><Edit className="w-4 h-4 text-gray-400" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { setSelected(trip); setDeleteOpen(true); }} data-testid={`delete-trip-${trip.id}`} title="Delete"><Trash2 className="w-4 h-4 text-gray-400" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Log Trip</DialogTitle>
            <DialogDescription>Record a vehicle trip with odometer readings</DialogDescription>
          </DialogHeader>
          <TripForm onSubmit={handleAdd} />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Update Trip</DialogTitle>
          </DialogHeader>
          <TripForm onSubmit={handleEdit} isEdit />
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trip?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this trip record. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-trip">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
